import React, { useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
  Alert, Badge, Button, Card, Group, Modal, Select, Stack, Text, Title, Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconCloudUpload, IconCoin, IconDownload } from '@tabler/icons-react';

import { submitGeneration, getArtifactUrl } from '../api_scripts/solnStudioClient';
import { FLOW_TYPES } from '../flowCatalog';
import { resolveFlowRefs } from '../utils/resolveFlowRefs';
import { formatUsd } from '../utils/format';
import useJobPoll from '../hooks/useJobPoll';
import JobStatusAlert from '../components/JobStatusAlert';
import SolnStudioImportModal from '../components/SolnStudioImportModal';
import { blockerCount } from './OpenQuestionsPanel';

// A build artifact representing a synthesized contact flow carries the Connect flow `Type` as its
// artifact.type (CONTACT_FLOW / CUSTOMER_QUEUE / … / CONTACT_FLOW_MODULE) — the full enum, not just the
// old binary pair. Used to pick flow rows out of the artifact list (and to exclude FLOW_PLAN etc.).
const FLOW_ARTIFACT_TYPES = new Set(FLOW_TYPES);

// Shared Build/Test tab: trigger the deterministic artifact machine + list/download the artifacts.
export default function GenerationPanel({ project, designModel, getApiToken, onChanged, kind, title, blurb, cta, artifactTypes }) {
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Instance-aware flow export: resolve flow references to the real Connect IDs from this instance's
  // import ledger before zipping. null = download raw (names, unresolved).
  const [resolveInstanceId, setResolveInstanceId] = useState(null);
  const [resolveNote, setResolveNote] = useState(null);

  // Shared job loop (P0.4): resumes an in-flight build/test job on mount; terminal result persists
  // until dismissed (onChanged is a background refresh — it no longer erases this panel, P0.3).
  const { job, stalled, start, dismiss } = useJobPoll({
    project,
    getApiToken,
    match: (j) => j.kind === kind,
    onTerminal: () => onChanged(),
  });

  // The per-instance import ledger (project.connectResources) — drives the ledger panel + the
  // instance-aware flow export. Each entry: { instanceId, instanceAlias, region, lastImportAt, resources }.
  const ledgerInstances = Object.values(project.connectResources || {});

  const artifacts = (project.artifacts || []).filter((a) => !artifactTypes || artifactTypes.includes(a.type));
  // Every flow type (CONTACT_FLOW / CUSTOMER_QUEUE / … / CONTACT_FLOW_MODULE), not just the old binary
  // pair — otherwise a Customer Queue / Hold / Whisper flow is silently absent from the Build tab list.
  const flowArtifacts = artifacts.filter((a) => FLOW_ARTIFACT_TYPES.has(a.type));
  // Flows that synthesized to importable JSON (have an S3 object) vs ones that failed (metadata-only).
  const downloadableFlows = flowArtifacts.filter((a) => a.s3Key);
  const cdkArtifact = artifacts.find((a) => a.type === 'CDK_PROJECT');
  // CONNECT_IMPORT is generated by Build but not shown in the artifact list (it's the Import-to-Connect
  // hand-off, not a download); its presence gates the "Import to Connect" button.
  const hasConnectImport = (project.artifacts || []).some((a) => a.type === 'CONNECT_IMPORT');
  // C.8: an unmapped-language Lex locale guess (or other build-time warning) rides on the CDK artifact.
  const cdkWarnings = cdkArtifact?.warnings || [];
  // C.10: the frontend's own lexStrategy would call this NO_LEX, but Build still emits a bot from
  // lexBots[] — surface the disagreement instead of letting it pass silently.
  const dtmfLexConflict = kind === 'build'
    && designModel?.meta?.interactionStyle === 'dtmf_only'
    && (designModel?.lexBots || []).length > 0;

  // §P2.5 approval soft gate (Build only): the API 409s a build whose CURRENT design version isn't
  // approved; the UI intercepts up front (same check) and offers "Build anyway" (overrideApproval).
  const [approvalGateOpen, setApprovalGateOpen] = useState(false);
  const designApproved = !!project.designApprovedVersion && project.designApprovedVersion === project.designModelVersion;
  // §4.5: unresolved BLOCKER-severity open questions — surfaced in the approval modal (still advisory).
  const blockers = kind === 'build' ? blockerCount(designModel?.openQuestions || []) : 0;

  const run = async ({ overrideApproval = false } = {}) => {
    if (kind === 'build' && !designApproved && !overrideApproval) {
      setApprovalGateOpen(true);
      return;
    }
    setBusy(true); setError(null);
    try {
      const res = await submitGeneration(project.projectId, { kind, mode: 'deep', overrideApproval }, getApiToken);
      start(res.jobId, { jobId: res.jobId, status: res.status });
    } catch (err) {
      setError(err.reason === 'locked' ? 'A job is already running on this project.' : err.message);
    } finally { setBusy(false); }
  };

  const download = async (a) => {
    try { const { url } = await getArtifactUrl(project.projectId, a.id, getApiToken); window.open(url, '_blank'); }
    catch (e) { setError(e.message); }
  };

  // Fetch an artifact's JSON via its presigned URL (used by the client-side zip builders below).
  const fetchArtifactJson = async (a) => {
    const { url } = await getArtifactUrl(project.projectId, a.id, getApiToken);
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to download ${a.id} (${res.status})`);
    return res.json();
  };

  const slug = (project.name || 'solution').replace(/[^a-z0-9_-]+/gi, '_').replace(/^_+|_+$/g, '') || 'solution';

  // Bundle every contact-flow artifact into a single .zip (one importable JSON per flow), built
  // client-side from the per-flow artifacts (jszip is already a dependency for the Lucid export).
  const downloadFlowsZip = async () => {
    setError(null); setResolveNote(null); setZipBusy('flows');
    // When an instance is selected, rewrite each flow's references (queue/hours/etc. names) to the real
    // Connect IDs captured in that instance's import ledger; collect any unresolved refs to warn about.
    const resourceMap = resolveInstanceId ? project.connectResources?.[resolveInstanceId]?.resources : null;
    try {
      const zip = new JSZip();
      let resolvedTotal = 0;
      const unresolved = [];
      // Fetch every flow artifact IN PARALLEL (they were downloaded one-at-a-time before — slow on a
      // 15-flow build), then fold the results in deterministic plan order so the ref-resolution note is stable.
      const fetched = await Promise.all(
        downloadableFlows.map(async (a) => ({ a, json: await fetchArtifactJson(a) }))
      );
      for (const { a, json: raw } of fetched) {
        let json = raw;
        if (resourceMap) {
          const r = resolveFlowRefs(json, resourceMap);
          json = r.json;
          resolvedTotal += r.resolved;
          for (const u of r.unresolved) unresolved.push({ flow: a.id.replace(/^flow-/, ''), ...u });
        }
        zip.file(`${a.id.replace(/^flow-/, '')}.json`, JSON.stringify(json, null, 2));
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      saveAs(blob, `${slug}-contact-flows.zip`);
      if (resourceMap) {
        const names = [...new Set(unresolved.map((u) => `${u.name} (${u.collection})`))];
        setResolveNote({
          resolved: resolvedTotal,
          unresolved: names,
        });
      }
    } catch (e) { setError(e.message); } finally { setZipBusy(false); }
  };

  // The CDK artifact stores a { files: { path: content } } map; zip it client-side into a clonable project.
  const downloadCdkZip = async () => {
    if (!cdkArtifact) return;
    setError(null); setZipBusy('cdk');
    try {
      const { files } = await fetchArtifactJson(cdkArtifact);
      const zip = new JSZip();
      for (const [path, content] of Object.entries(files || {})) zip.file(path, content);
      const blob = await zip.generateAsync({ type: 'blob' });
      saveAs(blob, `${slug}-cdk-project.zip`);
    } catch (e) { setError(e.message); } finally { setZipBusy(false); }
  };

  return (
    <Stack>
      <Card withBorder padding="lg" radius="md">
        <Group justify="space-between" mb="sm">
          <div>
            <Group gap="xs" align="center">
              <Title order={4}>{title}</Title>
              <Badge variant="light" color="gray" leftSection={<IconCoin size={12} />}>
                AI cost: {formatUsd(project.costByPhase?.[kind] ?? 0)} total
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">{blurb}</Text>
            <Text size="xs" c="dimmed" mt={4}>This phase is deterministic — no AI model calls, so cost is $0.</Text>
          </div>
          <Group gap="xs">
            {kind === 'build' && hasConnectImport && (
              <Button variant="light" color="orange" leftSection={<IconCloudUpload size={16} />}
                onClick={() => setImportOpen(true)}>
                Import to Connect
              </Button>
            )}
            <Button color="orange" loading={busy} onClick={() => run()} disabled={project.status === 'CREATED'}>{cta}</Button>
          </Group>
        </Group>
        {dtmfLexConflict && (
          <Alert color="yellow" icon={<IconAlertCircle size={16} />} mb="sm">
            This design's interaction style is <b>dtmf_only</b>, but {designModel.lexBots.length} lexBots
            {designModel.lexBots.length === 1 ? ' entry is' : ' entries are'} still defined — Build will
            still emit a Lex bot even though the Design tab treats this solution as no-Lex. Remove the
            unused lexBots entries, or change the interaction style if Lex is actually wanted.
          </Alert>
        )}
        {error && <Alert color="red" icon={<IconAlertCircle size={16} />}>{error}</Alert>}
        <JobStatusAlert job={job} stalled={stalled} title={`${kind} job`} onDismiss={dismiss} />
      </Card>

      {artifacts.length > 0 && (
        <Card withBorder padding="lg" radius="md">
          <Title order={5} mb="sm">Artifacts</Title>
          {kind === 'build' && ledgerInstances.length > 0 && (
            <Card withBorder padding="sm" radius="sm" mb="sm" bg="var(--mantine-color-gray-0)">
              <Text size="sm" fw={500} mb={4}>Imported to Connect</Text>
              <Stack gap={2}>
                {ledgerInstances.map((inst) => {
                  const counts = Object.entries(inst.resources || {})
                    .map(([type, byName]) => `${Object.keys(byName).length} ${type}`)
                    .join(' · ');
                  return (
                    <Text key={inst.instanceId} size="xs" c="dimmed">
                      <b>{inst.instanceAlias || inst.instanceId}</b>{counts ? ` — ${counts}` : ''}
                      {inst.lastImportAt ? ` · last import ${new Date(inst.lastImportAt).toLocaleString()}` : ''}
                    </Text>
                  );
                })}
              </Stack>
            </Card>
          )}
          {kind === 'build' && (
            <Group gap="xs" mb="sm" align="flex-end">
              {downloadableFlows.length > 0 && ledgerInstances.length > 0 && (
                <Select
                  size="xs" style={{ width: 240 }} clearable
                  label="Resolve flow refs for instance"
                  placeholder="Download raw (names)"
                  data={ledgerInstances.map((i) => ({ value: i.instanceId, label: i.instanceAlias || i.instanceId }))}
                  value={resolveInstanceId} onChange={setResolveInstanceId}
                />
              )}
              {downloadableFlows.length > 0 && (
                <Button size="xs" color="orange" leftSection={<IconDownload size={14} />}
                  loading={zipBusy === 'flows'} disabled={!!zipBusy} onClick={downloadFlowsZip}>
                  {resolveInstanceId ? 'Download flows (IDs resolved) (.zip)' : 'Download all contact flows (.zip)'}
                </Button>
              )}
              {cdkArtifact ? (
                <Button size="xs" variant="light" color="blue" leftSection={<IconDownload size={14} />}
                  loading={zipBusy === 'cdk'} disabled={!!zipBusy} onClick={downloadCdkZip}>
                  Download CDK project (.zip)
                </Button>
              ) : (
                <Text size="xs" c="dimmed">
                  No CDK project needed — this solution has no Lex or Lambda resources.
                </Text>
              )}
            </Group>
          )}
          {kind === 'build' && cdkWarnings.length > 0 && (
            <Alert color="yellow" mb="sm" icon={<IconAlertCircle size={16} />}>
              <Text size="sm" fw={500}>CDK build warning(s):</Text>
              {cdkWarnings.map((w, i) => <Text key={i} size="xs" c="dimmed">{w}</Text>)}
            </Alert>
          )}
          {kind === 'build' && resolveNote && (
            <Alert color={resolveNote.unresolved.length ? 'yellow' : 'green'} mb="sm" icon={<IconAlertCircle size={16} />}>
              <Text size="sm">Resolved {resolveNote.resolved} flow reference(s) to imported Connect IDs.</Text>
              {resolveNote.unresolved.length > 0 && (
                <Text size="xs" c="dimmed" mt={4}>
                  Unresolved (not in this instance's import ledger — e.g. contact-flow targets, which aren't
                  imported by this tool yet): {resolveNote.unresolved.slice(0, 12).join(', ')}
                  {resolveNote.unresolved.length > 12 ? ` +${resolveNote.unresolved.length - 12} more` : ''}.
                </Text>
              )}
            </Alert>
          )}
          <Stack gap="xs">
            {artifacts.map((a) => {
              // A flow that failed to synthesize has valid:false and no s3Key — show why, no download.
              const failedSynth = a.valid === false && !a.s3Key;
              return (
                <Group key={a.id} justify="space-between">
                  <Group gap="xs">
                    <Badge variant="light" color={a.type === 'CDK_PROJECT' ? 'blue' : a.type === 'SIT_PLANS' ? 'violet' : a.type === 'NLU_TEST_SET' ? 'teal' : 'orange'}>{a.type}</Badge>
                    <Text size="sm">{a.id}{a.count != null ? ` (${a.count})` : ''}</Text>
                    {failedSynth ? (
                      <Tooltip
                        label={(a.errors || []).join('; ') || 'Could not synthesize to importable JSON'}
                        withArrow multiline w={320}
                      >
                        <Badge color="red" size="xs">failed to synthesize</Badge>
                      </Tooltip>
                    ) : a.valid === false ? (
                      <Tooltip label={(a.errors || []).join('; ') || 'Flow did not validate'} withArrow multiline w={320}>
                        <Badge color="red" size="xs">invalid</Badge>
                      </Tooltip>
                    ) : null}
                  </Group>
                  {!failedSynth && (
                    <Button size="xs" variant="subtle" color="orange"
                      onClick={() => (a.type === 'CDK_PROJECT' ? downloadCdkZip() : download(a))}>Download</Button>
                  )}
                </Group>
              );
            })}
          </Stack>
        </Card>
      )}

      {kind === 'build' && (
        <Modal opened={approvalGateOpen} onClose={() => setApprovalGateOpen(false)} title="Design not approved" centered>
          <Stack>
            <Text size="sm">
              {project.designApprovedVersion
                ? <>The design has changed since <b>v{project.designApprovedVersion}</b> was approved (it is now
                    v{project.designModelVersion}). Re-approve it on the Design tab, or build anyway.</>
                : <>Design <b>v{project.designModelVersion || 0}</b> hasn't been approved yet. The intended flow is
                    review → <b>Approve design</b> on the Design tab → Build. You can also build anyway.</>}
            </Text>
            {blockers > 0 && (
              <Alert color="red" icon={<IconAlertCircle size={16} />}>
                <b>{blockers} blocker-severity open question(s)</b> are still unresolved. These are gaps the
                Build genuinely needs answered (e.g. a missing integration contract). Resolving them on the
                Design tab first is recommended — this is advisory, you can still build anyway.
              </Alert>
            )}
            <Group justify="flex-end" gap="xs">
              <Button variant="subtle" color="gray" onClick={() => setApprovalGateOpen(false)}>Cancel</Button>
              <Button color="orange" onClick={() => { setApprovalGateOpen(false); run({ overrideApproval: true }); }}>
                Build anyway
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}

      {kind === 'build' && (
        <SolnStudioImportModal
          opened={importOpen}
          onClose={() => setImportOpen(false)}
          project={project}
          getApiToken={getApiToken}
          onImported={onChanged}
        />
      )}
    </Stack>
  );
}
